import { defineStore } from "pinia";
import api from "@/services/api";
import {
  buildQueryString,
  mapTranslationToEventRecord,
  buildEventHierarchy,
  type ActivityQueryParams,
  type ChildEventsQueryParams,
  type EventsResult,
  type EventRecord,
  type EventTranslationResponse,
  type EventHierarchyEntry,
  type UserOption,
} from "@bono-packages/activity-log";

/**
 * Activity Log store.
 *
 * All activity-service traffic goes through the app's shared axios instance (`@/services/api`)
 * so it inherits the auth token, the `X-COMPANY-ID` company-scoping header, and the 401
 * refresh/retry interceptor. The `@bono-packages/activity-log` package itself makes no HTTP
 * calls — the component receives `events` as props and its emits are wired to these actions.
 *
 * Company/organization isolation is enforced server-side by the bono-backend proxy from the
 * authenticated principal; the frontend does not (and must not) send an organization id to
 * scope a regular user.
 */
export const useActivityLogStore = defineStore("activityLog", () => {
  async function fetchEvents(params: ActivityQueryParams = {}): Promise<EventsResult> {
    const queryString = buildQueryString(params);
    const url = `/api/activity/events${queryString ? `?${queryString}` : ""}`;
    const { data } = await api.get<EventTranslationResponse[]>(url);
    const flatEvents = (data ?? []).map(mapTranslationToEventRecord);
    const fetchedCount = flatEvents.length;
    // parentsOnly=true → already flat parents; otherwise reconstruct the parent/child tree.
    const events = params.parentsOnly ? flatEvents : buildEventHierarchy(flatEvents);
    return { events, fetchedCount };
  }

  async function fetchChildEvents(params: ChildEventsQueryParams): Promise<EventRecord[]> {
    const searchParams = new URLSearchParams();
    searchParams.append("causationId", params.causationId);
    if (params.language) {
      searchParams.append("language", params.language);
    }
    if (params.includeConsumerStatuses !== undefined) {
      searchParams.append("includeConsumerStatuses", String(params.includeConsumerStatuses));
    }
    const { data } = await api.get<EventTranslationResponse[]>(
      `/api/activity/events/children?${searchParams.toString()}`
    );
    return (data ?? []).map(mapTranslationToEventRecord);
  }

  // The whole Domain > Resource > EventType catalog in one request. The view derives all three
  // dropdown levels from it in memory, so picking a domain or resource costs no HTTP call.
  //
  // Replaces the three per-level `/event-hierarchy/*` calls this store used to make. Those read the
  // RET_DOMAIN_RESOURCE / RET_RESOURCE_EVENT_TYPE join tables, which drift behind
  // ret_event_dictionary and carry no domain, and they filter by access rules that most dictionary
  // records simply do not have — so they hid most of the catalog. This route reads the dictionary
  // itself and bono-backend pins it to PUBLIC visibility.
  async function fetchEventCatalog(): Promise<EventHierarchyEntry[]> {
    const { data } = await api.get<EventHierarchyEntry[]>("/event-hierarchy/catalog");
    return data ?? [];
  }

  // Distinct users that appear in the caller's activity records (org-scoped server-side).
  // The returned id equals ret_event_record.user_id, so it matches the events `userIds` filter.
  async function fetchActivityUsers(): Promise<UserOption[]> {
    const { data } = await api.get<{ userId: string; userName: string }[]>(
      "/api/activity/events/users"
    );
    return (data ?? []).map((u) => ({ id: u.userId, name: u.userName || u.userId }));
  }

  return {
    fetchEvents,
    fetchChildEvents,
    fetchEventCatalog,
    fetchActivityUsers,
  };
});
